import { useState } from 'react';
import { Coins, Infinity as InfinityIcon, Sparkles, TriangleAlert, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAiCreditsStatus } from '@/hooks/queries/useAiCredits';
import { AiCreditsPurchaseSheet } from './AiCreditsPurchaseSheet';

interface ChatQuotaBannerProps {
  compact?: boolean;
}

export function ChatQuotaBanner({ compact = false }: ChatQuotaBannerProps) {
  const { data: status } = useAiCreditsStatus();
  const [purchaseOpen, setPurchaseOpen] = useState(false);

  if (!status) return null;

  // Illimité : badge discret
  if (status.unlimited) {
    return (
      <div className="mx-auto w-full max-w-lg mb-2 flex items-center gap-1.5 rounded-full px-3 py-1 bg-secondary-50 dark:bg-secondary-900/20 text-xs font-medium text-secondary-900 dark:text-secondary-100 shadow-2xs border-0">
        <InfinityIcon className="size-3.5 text-secondary-700 dark:text-secondary-300" aria-hidden />
        <span>Crédits illimités</span>
      </div>
    );
  }

  // Owner avec solde connu
  if (status.balance !== undefined) {
    const isExhausted = status.balance <= 0;
    const isLow = status.balance > 0 && status.balance <= 10;

    return (
      <>
        <div
          className={cn(
            'mx-auto w-full max-w-lg mb-2 flex items-center justify-between gap-2 rounded-full px-3.5 py-1.5 text-xs shadow-2xs border-0 transition-all',
            isExhausted
              ? 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300'
              : isLow
                ? 'bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-300'
                : 'bg-white text-slate-700 dark:bg-charcoal-800 dark:text-slate-200',
          )}
        >
          <div className="flex items-center gap-1.5 min-w-0 font-medium">
            {isExhausted ? (
              <TriangleAlert className="size-3.5 shrink-0 text-rose-600 dark:text-rose-400" />
            ) : (
              <Coins className="size-3.5 shrink-0 text-[#76b900]" />
            )}
            <span className="truncate">
              {isExhausted
                ? 'Crédits épuisés'
                : `${status.balance} crédit${status.balance !== 1 ? 's' : ''} restant${status.balance !== 1 ? 's' : ''}`}
            </span>
          </div>

          {(isExhausted || isLow || compact) && (
            <button
              type="button"
              onClick={() => setPurchaseOpen(true)}
              className="shrink-0 rounded-full bg-white dark:bg-charcoal-700 px-3 py-0.5 text-[11px] font-bold shadow-2xs hover:bg-slate-50 transition-colors"
            >
              Recharger
            </button>
          )}
        </div>

        <AiCreditsPurchaseSheet open={purchaseOpen} onOpenChange={setPurchaseOpen} />
      </>
    );
  }

  // Employé : affiche disponibilité
  const isAvailable = status.available;

  return (
    <div
      className={cn(
        'mx-auto w-full max-w-lg mb-2 flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-medium shadow-2xs border-0',
        isAvailable
          ? 'bg-white text-slate-700 dark:bg-charcoal-800 dark:text-slate-200'
          : 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300',
      )}
    >
      {isAvailable ? (
        <Sparkles className="size-3.5 shrink-0 text-[#76b900]" aria-hidden />
      ) : (
        <Zap className="size-3.5 shrink-0 text-rose-600" aria-hidden />
      )}
      <span className="truncate">
        {isAvailable ? 'IA disponible' : 'Crédits épuisés — contactez le propriétaire'}
      </span>
    </div>
  );
}
